import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  PlayCircle,
  ShieldCheck,
  Brain,
  ScanLine,
  Star,
  AudioLines,
  History,
  Siren,
  BadgeIndianRupee,
} from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

/**
 * HeroAI — the new front door of /IN.
 *
 * Replaces the older "No More Cheating" emotional hero with a confident,
 * AI-first claim ("Don't trust your eyes. Trust our AI.") combined with the
 * 247-checkpoint dial visualization that previously lived in <AITechnology />.
 *
 * Two priorities:
 *   1. Cinematic first impression — Apple-keynote dark, generous whitespace,
 *      one massive number rotating with a sweeping scan beam.
 *   2. Conversion path — primary "Book Inspection" + secondary "View Sample
 *      Report" CTAs sit immediately under the headline, never below the fold.
 *
 * Receives the same props as the legacy <Hero /> (onBookInspection,
 * onViewSampleReport) so swap-in is one-line in Home.jsx.
 */
const HeroAI = ({ onBookInspection, onViewSampleReport }) => {
  const { formatPrice, t } = useCurrency();
  void t;

  const orbitChips = [
    { label: 'Engine',     deg: -90, color: 'text-blue-300' },
    { label: 'Body',       deg: -32, color: 'text-emerald-300' },
    { label: 'OBD-2',      deg:  35, color: 'text-amber-300' },
    { label: 'Electrical', deg:  92, color: 'text-purple-300' },
    { label: 'Documents',  deg: 150, color: 'text-rose-300' },
    { label: 'Frame',      deg: 215, color: 'text-cyan-300' },
  ];

  return (
    <section
      className="relative bg-slate-950 text-white overflow-hidden pt-24 sm:pt-28 lg:pt-32 pb-16 lg:pb-20"
      data-testid="hero-ai-section"
    >
      {/* Background — soft radial blooms + a fine-line grid */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-blue-600/15 blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-[420px] h-[420px] rounded-full bg-emerald-500/10 blur-3xl" />
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.04]"
          viewBox="0 0 1200 800"
          aria-hidden="true"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <pattern id="hero-grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="white" strokeWidth="0.6" />
            </pattern>
          </defs>
          <rect width="1200" height="800" fill="url(#hero-grid)" />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* LEFT — Copy + CTAs (always first; on mobile stacks above the dial) */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-blue-400/30 bg-blue-500/10 text-[11px] font-bold uppercase tracking-[0.22em] text-blue-300"
            >
              <Brain className="h-3.5 w-3.5" strokeWidth={1.8} />
              India&apos;s First AI Pre-Purchase Inspection
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.05 }}
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.04]"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              Don&apos;t trust your eyes.
              <br />
              <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-emerald-300 bg-clip-text text-transparent">
                Trust our AI.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.55, delay: 0.15 }}
              className="mt-6 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed"
            >
              247-checkpoint AI inspection trained on{' '}
              <strong className="text-white">2,00,000+ real cars</strong>.
              Catches what mechanics miss — odometer fraud, hidden accidents,
              flood damage, engine wear. Report in <strong className="text-white">1 hour</strong>.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg"
            >
              <button
                onClick={onBookInspection}
                data-testid="hero-book-inspection-btn"
                className="flex-1 inline-flex items-center justify-center gap-1.5 whitespace-nowrap px-5 sm:px-6 py-4 rounded-xl bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-extrabold text-sm sm:text-base tracking-tight transition-all shadow-[0_10px_30px_-6px_rgba(37,99,235,0.5)] hover:-translate-y-[2px]"
              >
                Book Inspection
                <span className="font-mono font-bold opacity-90">· {formatPrice(1300)}</span>
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <button
                onClick={onViewSampleReport}
                data-testid="hero-view-report-btn"
                className="flex-1 inline-flex items-center justify-center gap-2 whitespace-nowrap px-4 sm:px-5 py-4 rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur-sm border border-white/15 text-white font-bold text-sm sm:text-base transition-all"
              >
                <PlayCircle className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
                View Sample Report
              </button>
            </motion.div>

            {/* Trust pills */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mt-8 flex flex-wrap gap-2"
            >
              <Pill icon={Star}             text="4.8★ on Google · 3,200+ reviews" />
              <Pill icon={History}          text="Service History" />
              <Pill icon={AudioLines}       text="Engine Sound AI Analysis" />
              <Pill icon={ShieldCheck}      text="Free 1-Year Engine Warranty" />
              <Pill icon={Siren}            text="Traffic Challans & Accident History" />
              <Pill icon={BadgeIndianRupee} text="Purchase Price Recommendation" />
            </motion.div>
          </div>

          {/* RIGHT — The dial (visual centerpiece) */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-[340px] sm:max-w-[440px] lg:max-w-[520px] aspect-square">
              {/* Outer rotating ring */}
              <motion.div
                className="absolute inset-0"
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
              >
                <svg viewBox="0 0 600 600" className="w-full h-full" aria-hidden="true">
                  <circle cx="300" cy="300" r="280" fill="none" stroke="rgba(96,165,250,0.25)" strokeWidth="1" strokeDasharray="2 8" />
                  <circle cx="300" cy="300" r="240" fill="none" stroke="rgba(52,211,153,0.18)" strokeWidth="1" strokeDasharray="6 6" />
                </svg>
              </motion.div>

              {/* Counter-rotating ring with tick marks */}
              <motion.div
                className="absolute inset-8"
                animate={{ rotate: -360 }}
                transition={{ duration: 90, repeat: Infinity, ease: 'linear' }}
              >
                <svg viewBox="0 0 600 600" className="w-full h-full" aria-hidden="true">
                  <circle cx="300" cy="300" r="220" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />
                  {[...Array(48)].map((_, i) => {
                    const angle = (i * 360) / 48;
                    const rad = (angle * Math.PI) / 180;
                    const x1 = 300 + Math.cos(rad) * 215;
                    const y1 = 300 + Math.sin(rad) * 215;
                    const x2 = 300 + Math.cos(rad) * (i % 4 === 0 ? 200 : 208);
                    const y2 = 300 + Math.sin(rad) * (i % 4 === 0 ? 200 : 208);
                    return (
                      <line
                        key={i}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={i % 4 === 0 ? 'rgba(96,165,250,0.6)' : 'rgba(255,255,255,0.18)'}
                        strokeWidth="1"
                      />
                    );
                  })}
                </svg>
              </motion.div>

              {/* Sweeping scan beam */}
              <motion.div
                className="absolute inset-0"
                animate={{ rotate: 360 }}
                transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
              >
                <div className="absolute left-1/2 top-1/2 w-[230px] h-[2px] origin-left bg-gradient-to-r from-cyan-400 via-cyan-400/50 to-transparent shadow-[0_0_12px_rgba(34,211,238,0.6)]" />
              </motion.div>

              {/* Centre — the big claim */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-300/80 mb-2">
                  <ScanLine className="inline h-3 w-3 mr-1.5 -mt-px" strokeWidth={2} />
                  Checkpoints
                </span>
                <motion.div
                  initial={{ scale: 0.85, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  className="font-black tracking-tighter leading-none text-[110px] sm:text-[130px] lg:text-[150px] bg-gradient-to-b from-white via-white to-slate-500 bg-clip-text text-transparent"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  247
                </motion.div>
                <span className="mt-1 text-xs font-semibold text-slate-400 max-w-[200px]">
                  Every car. Every time.
                </span>
              </div>

              {/* Orbital category chips */}
              {orbitChips.map((c, i) => {
                const r = 215; // pixels — scales with container at runtime via `vw`-like
                const rad = (c.deg * Math.PI) / 180;
                // Use a percentage of container so chips stay near the outer ring
                const xPct = 50 + (Math.cos(rad) * 47);
                const yPct = 50 + (Math.sin(rad) * 47);
                void r;
                return (
                  <motion.div
                    key={c.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.45 + i * 0.06 }}
                    className="absolute px-2.5 py-1 rounded-full border border-white/15 bg-slate-900/85 backdrop-blur-md whitespace-nowrap shadow-[0_4px_18px_-4px_rgba(0,0,0,0.6)]"
                    style={{
                      left: `${xPct}%`,
                      top: `${yPct}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <span className={`text-[10px] font-bold uppercase tracking-[0.16em] ${c.color}`}>
                      {c.label}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Pill = ({ icon: Icon, text }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.07] border border-white/15 text-xs font-semibold text-slate-200 backdrop-blur-sm">
    <Icon className="h-3.5 w-3.5 text-cyan-300" />
    {text}
  </div>
);

export default HeroAI;
